import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { sendEmail, ADMIN_EMAIL } from '../../lib/emailService';

const EVENT_TYPES = [
  'Birthday',
  'Wedding',
  'Corporate Event',
  'Family Gathering',
  'Holiday Party',
  'School / Community Event',
  'Other',
];

export default function CateringInquiryForm({ onBack }) {
  const [form, setForm] = useState({
    name:       '',
    email:      '',
    phone:      '',
    event_date: '',
    headcount:  '',
    event_type: '',
    notes:      '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState('');

  const setF = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email);
  const isValid      = form.name.trim() && isEmailValid;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid) return;
    setSubmitting(true);
    setError('');

    try {
      if (isSupabaseConfigured()) {
        const { error: err } = await supabase.from('catering_inquiries').insert({
          name:       form.name.trim(),
          email:      form.email.trim().toLowerCase(),
          phone:      form.phone.trim()        || null,
          event_date: form.event_date          || null,
          headcount:  form.headcount ? parseInt(form.headcount) : null,
          event_type: form.event_type          || null,
          notes:      form.notes.trim()        || null,
          status:     'new',
        });
        if (err) throw err;

        // Notify admin (fire-and-forget)
        sendEmail(
          ADMIN_EMAIL,
          `New Catering Inquiry from ${form.name}`,
          `<p>A new catering inquiry was just submitted:</p>
           <table style="border-collapse:collapse;font-family:sans-serif;font-size:14px">
             <tr><td style="padding:6px 12px;font-weight:bold;color:#6b8262">Name</td><td style="padding:6px 12px">${form.name}</td></tr>
             <tr><td style="padding:6px 12px;font-weight:bold;color:#6b8262">Email</td><td style="padding:6px 12px">${form.email}</td></tr>
             <tr><td style="padding:6px 12px;font-weight:bold;color:#6b8262">Phone</td><td style="padding:6px 12px">${form.phone || 'N/A'}</td></tr>
             <tr><td style="padding:6px 12px;font-weight:bold;color:#6b8262">Event Date</td><td style="padding:6px 12px">${form.event_date || 'TBD'}</td></tr>
             <tr><td style="padding:6px 12px;font-weight:bold;color:#6b8262">Headcount</td><td style="padding:6px 12px">${form.headcount || 'TBD'}</td></tr>
             <tr><td style="padding:6px 12px;font-weight:bold;color:#6b8262">Event Type</td><td style="padding:6px 12px">${form.event_type || 'N/A'}</td></tr>
             <tr><td style="padding:6px 12px;font-weight:bold;color:#6b8262;vertical-align:top">Notes</td><td style="padding:6px 12px">${form.notes || 'None'}</td></tr>
           </table>`
        ).catch(() => {});
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Success screen ───────────────────────────────────────────────────────
  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-16 space-y-6 max-w-md mx-auto"
      >
        <div className="w-24 h-24 bg-orange-50 rounded-full flex items-center justify-center mx-auto">
          <SafeIcon name="Check" className="text-5xl text-orange-400" />
        </div>
        <h2 className="text-4xl font-serif text-charcoal">Inquiry Sent!</h2>
        <p className="text-sage-500 text-lg">
          Thank you, {form.name.split(' ')[0]}! We'll be in touch within 24–48 hours to discuss your event.
        </p>
        <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5 text-left space-y-2">
          <p className="text-xs font-bold text-sage-400 uppercase tracking-widest">What happens next?</p>
          <p className="text-sm text-charcoal">
            We'll review your request and reach out to <strong>{form.email}</strong> with availability, menu options, and pricing.
          </p>
        </div>
        <button
          onClick={() => window.location.hash = '#/'}
          className="btn-primary flex items-center justify-center gap-2 mx-auto"
        >
          Back to Home <SafeIcon name="ArrowRight" />
        </button>
      </motion.div>
    );
  }

  // ── Inquiry form ─────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-8 max-w-lg mx-auto"
    >
      <div className="text-center space-y-3">
        <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto">
          <SafeIcon name="Star" className="text-orange-400 text-3xl" />
        </div>
        <h2 className="text-4xl font-serif text-charcoal">Tell Us About Your Event</h2>
        <p className="text-sage-500 text-lg">
          We'd love to cater your next event. Fill in the details and we'll be in touch.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-2xl px-5 py-3 text-red-600 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-3xl border border-sage-100 p-8 shadow-soft">

        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-2">Your Name *</label>
          <input
            type="text"
            className="input-field w-full"
            value={form.name}
            onChange={e => setF('name', e.target.value)}
            placeholder="Full name"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-2">Email Address *</label>
          <input
            type="email"
            className="input-field w-full"
            value={form.email}
            onChange={e => setF('email', e.target.value)}
            placeholder="you@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-2">Phone Number</label>
          <input
            type="tel"
            className="input-field w-full"
            value={form.phone}
            onChange={e => setF('phone', e.target.value)}
            placeholder="(555) 000-0000"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-2">Event Date</label>
            <input
              type="date"
              className="input-field w-full"
              value={form.event_date}
              onChange={e => setF('event_date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-2">Guest Count</label>
            <input
              type="number"
              className="input-field w-full"
              value={form.headcount}
              onChange={e => setF('headcount', e.target.value)}
              placeholder="50"
              min={1}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-2">Event Type</label>
          <select
            className="input-field w-full"
            value={form.event_type}
            onChange={e => setF('event_type', e.target.value)}
          >
            <option value="">Select event type...</option>
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-2">Tell Us More</label>
          <textarea
            className="input-field w-full min-h-[100px] resize-none"
            value={form.notes}
            onChange={e => setF('notes', e.target.value)}
            placeholder="Special requests, dietary restrictions, preferred menu items, questions..."
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
          <button type="button" onClick={onBack} className="btn-secondary">
            Back
          </button>
          <button
            type="submit"
            disabled={!isValid || submitting}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending…</>
            ) : (
              <><SafeIcon name="Send" /> Send Inquiry</>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
}
